/**
 * CopyButton — copies a value to the clipboard and confirms with a toast, briefly
 * swapping its icon to a check.
 *
 * Desk work is copy-paste heavy (account numbers, wallet addresses, amounts,
 * reference codes): one tap, zero transcription errors. Icon-only by default;
 * pass `label` for a text button.
 */
import { Show, createSignal, onCleanup, splitProps, type JSX } from "solid-js";
import Copy from "lucide-solid/icons/copy";
import Check from "lucide-solid/icons/check";
import { cn } from "./utils/cn";
import { focusRing } from "./utils/focus-ring";
import { toast } from "./toast";

export interface CopyButtonProps
  extends Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, "value" | "children"> {
  /** The exact text placed on the clipboard. */
  value: string;
  /** Optional visible label (icon-only when omitted). */
  label?: string;
  /** Toast shown on success. Defaults to "Copied". */
  copiedMessage?: string;
  /** Icon size in px. */
  size?: number;
}

export function CopyButton(props: CopyButtonProps): JSX.Element {
  const [local, rest] = splitProps(props, [
    "value",
    "label",
    "copiedMessage",
    "size",
    "class",
  ]);
  const [copied, setCopied] = createSignal(false);
  let timer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(timer));

  async function copy(e: MouseEvent) {
    e.stopPropagation();
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(local.value);
      setCopied(true);
      toast.success(local.copiedMessage ?? "Copied");
      clearTimeout(timer);
      timer = setTimeout(() => setCopied(false), 1200);
    } catch {
      toast.error("Couldn't copy to clipboard");
    }
  }

  const px = () => local.size ?? 15;

  return (
    <button
      type="button"
      {...rest}
      onClick={copy}
      aria-label={local.label ? undefined : "Copy"}
      title="Copy to clipboard"
      class={cn(
        "c9-touch inline-flex shrink-0 items-center justify-center gap-050 rounded-[3px]",
        "text-[color:var(--c9-text-subtle)] transition-colors",
        "hover:bg-[var(--c9-bg-neutral-subtle-hovered)] hover:text-[color:var(--c9-text)] active:bg-[var(--c9-bg-neutral-subtle-pressed)]",
        local.label ? "h-6 px-100 text-body-s font-[570]" : "size-6",
        focusRing,
        local.class,
      )}
    >
      <Show when={copied()} fallback={<Copy size={px()} />}>
        <Check size={px()} style={{ color: "var(--c9-text-success)" }} />
      </Show>
      <Show when={local.label}>{local.label}</Show>
    </button>
  );
}
